import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, CheckCircle2, MessageSquare, Users } from "lucide-react";
import { useAuth } from "../AuthContext";
import api, { apiError, calcAge, fmtDate } from "../api";
import { Card, PageHeader, Button, Input, Select, Field } from "../components/ui";
import DocUploadSlot from "../components/DocUploadSlot";

const CATEGORIES = [
  { key: "declaration", label: "Candidate Declaration Form", hint: "PDF only", accept: ".pdf" },
  { key: "ilo_page", label: "ILO Page", hint: "PDF or Image (JPG/PNG)", accept: ".pdf,.jpg,.jpeg,.png" },
  { key: "medical_reports", label: "Medical Reports", hint: "PDF only", accept: ".pdf" },
  { key: "consent_form", label: "MedVerify Consent Form", hint: "PDF only", accept: ".pdf" },
  { key: "additional_tests", label: "Prescription / Additional Tests", hint: "PDF or Image (JPG/PNG)", accept: ".pdf,.jpg,.jpeg,.png" },
];

function StepIndicator({ step }) {
  return (
    <div className="mb-6 flex items-center gap-3">
      {[["1", "Candidate Details"], ["2", "Rank, Company & Services"], ["3", "Document Uploads"]].map(([n, label], i) => (
        <React.Fragment key={n}>
          {i > 0 && <div className="h-px w-10 bg-slate-300" />}
          <div className={`flex items-center gap-2 ${step === i + 1 ? "" : "opacity-50"}`}>
            <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${step > i + 1 ? "bg-emerald-500 text-white" : step === i + 1 ? "bg-navy text-white" : "bg-slate-200 text-slate-600"}`}>
              {step > i + 1 ? <CheckCircle2 size={14} /> : n}
            </span>
            <span className="text-sm font-semibold text-slate-700">{label}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

export default function RegisterCandidate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const [searchParams] = useSearchParams();
  const wantStep2 = searchParams.get("step") === "2";

  const [step, setStep] = useState(1);
  const [candidate, setCandidate] = useState(null);
  const [clients, setClients] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [pandiClubs, setPandiClubs] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [services, setServices] = useState([]);
  const [dupes, setDupes] = useState([]);
  const [rosterMatches, setRosterMatches] = useState([]);
  const [rosterEntryId, setRosterEntryId] = useState("");
  const skipRosterRef = useRef(false);
  const [hiddenDocIds, setHiddenDocIds] = useState(null);
  const [remark, setRemark] = useState("");
  const [form, setForm] = useState({
    name: "", company_id: "", rank: "", dob: "", mobile: "",
    indos_number: "", cdc_number: "", client_org_id: "", clinic_org_id: "", service_ids: [],
    pandi_org_id: "", vessel: "",
  });
  const [busy, setBusy] = useState(false);

  const age = calcAge(form.dob);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/pandi-clubs").then((r) => setPandiClubs(r.data)).catch(() => {});
    api.get("/ranks").then((r) => setRanks(r.data)).catch(() => {});
    if (user.role === "admin") api.get("/clinics").then((r) => setClinics(r.data)).catch(() => {});
  }, [user.role]);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/candidates/${editId}`).then((r) => {
      const c = r.data;
      setCandidate(c);
      const resume = user.role === "clinic" && c.status === "pending" && !c.is_final && c.clinic_org_id === user.organization_id;
      setForm({
        name: c.name, company_id: c.company_id, rank: c.rank, dob: c.dob, mobile: c.mobile,
        indos_number: c.indos_number || "", cdc_number: c.cdc_number || "",
        client_org_id: user.role === "admin" || resume ? c.client_org_id : "",
        clinic_org_id: c.clinic_org_id,
        service_ids: user.role === "admin" || resume ? c.service_ids || [] : [],
        pandi_org_id: user.role === "admin" || resume ? c.pandi_org_id || "" : "",
        vessel: user.role === "admin" || resume ? c.vessel || "" : "",
      });
      if (wantStep2 || user.role !== "admin") {
        setHiddenDocIds((prev) => prev ?? (resume ? new Set() : new Set((c.documents || []).map((d) => d.id))));
      }
      if (wantStep2) setStep(2);
    }).catch((e) => toast.error(apiError(e)));
  }, [isEdit, editId, wantStep2, user.role]);

  useEffect(() => {
    if (!form.client_org_id) { setServices([]); return; }
    api.get(`/clients/${form.client_org_id}/services`).then((r) => setServices(r.data)).catch(() => setServices([]));
  }, [form.client_org_id]);

  // duplicate detection
  useEffect(() => {
    const hasKey = form.indos_number.trim() || form.cdc_number.trim() || form.mobile.trim().length >= 6;
    if (!hasKey) { setDupes([]); return; }
    const t = setTimeout(() => {
      api.get("/candidates/check-duplicate", {
        params: {
          indos_number: form.indos_number.trim() || undefined,
          cdc_number: form.cdc_number.trim() || undefined,
          mobile: form.mobile.trim().length >= 6 ? form.mobile.trim() : undefined,
          exclude_id: isEdit ? editId : undefined,
        },
      }).then((r) => setDupes(r.data)).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [form.indos_number, form.cdc_number, form.mobile, isEdit, editId]);

  // imported crew list lookup (auto-fill)
  useEffect(() => {
    if (isEdit) return;
    if (skipRosterRef.current) { skipRosterRef.current = false; setRosterMatches([]); return; }
    const q = form.indos_number.trim().length >= 2 ? form.indos_number.trim()
      : form.mobile.trim().length >= 4 ? form.mobile.trim()
      : form.name.trim().length >= 2 ? form.name.trim() : "";
    if (!q) { setRosterMatches([]); return; }
    const t = setTimeout(() => {
      api.get("/roster/lookup", { params: { q } }).then((r) => setRosterMatches(r.data)).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [form.name, form.indos_number, form.mobile, isEdit]);

  const fillFromRoster = (m) => {
    skipRosterRef.current = true;
    setRosterEntryId(m.id);
    setForm((f) => ({
      ...f, name: m.name, rank: m.rank || f.rank, dob: m.dob || f.dob,
      mobile: m.mobile || f.mobile, indos_number: m.indos_number || f.indos_number,
      cdc_number: m.cdc_number || f.cdc_number, company_id: m.company_id || f.company_id,
      client_org_id: m.client_org_id || f.client_org_id,
    }));
    setRosterMatches([]);
    toast.success("Details filled from imported crew list — review & edit if needed");
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value, ...(k === "client_org_id" ? { service_ids: [] } : {}) });

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      service_ids: f.service_ids.includes(id) ? f.service_ids.filter((s) => s !== id) : [...f.service_ids, id],
    }));
  };

  const refreshCandidate = useCallback((cid) => {
    api.get(`/candidates/${cid}`).then((r) => setCandidate(r.data)).catch(() => {});
  }, []);

  // live updates: keep candidate state (verdict / cancel eligibility) fresh
  useEffect(() => {
    if (!candidate) return;
    const t = setInterval(() => refreshCandidate(candidate.id), 10000);
    return () => clearInterval(t);
  }, [candidate && candidate.id, refreshCandidate]);

  const submitStep1 = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(form.mobile)) return toast.error("Mobile number must be exactly 10 digits");
    const dob = new Date(form.dob);
    const minDob = new Date(); minDob.setFullYear(minDob.getFullYear() - 106);
    if (!form.dob || isNaN(dob) || dob >= new Date()) return toast.error("Enter a valid date of birth");
    if (dob < minDob) return toast.error("Date of birth cannot be more than 106 years ago");
    if (isEdit && user.role === "admin") {
      if (!form.client_org_id) return toast.error("Select a client (shipping company)");
      if (form.service_ids.length === 0) return toast.error("Select at least one service for this candidate");
      if (form.pandi_org_id && !form.vessel.trim()) return toast.error("Vessel is mandatory when a P&I club is selected");
    }
    setBusy(true);
    try {
      if (isEdit) {
        if (user.role === "clinic") {
          await api.put(`/candidates/${editId}`, { company_id: form.company_id, mobile: form.mobile, cdc_number: form.cdc_number });
          toast.success("Details updated");
          refreshCandidate(editId);
          setStep(2);
          return;
        }
        await api.put(`/candidates/${editId}`, form);
        toast.success("Candidate updated");
        navigate(`/candidates/${editId}`);
        return;
      }
      const { data } = await api.post("/candidates", { ...form, roster_entry_id: rosterEntryId });
      toast.success("Candidate registered");
      refreshCandidate(data.id);
      setStep(2);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveServices = async () => {
    if (!candidate) return;
    if (!form.rank.trim()) return toast.error("Enter the candidate's rank");
    if (!form.client_org_id) return toast.error("Select a client (shipping company)");
    if (form.service_ids.length === 0) return toast.error("Select at least one service for this candidate");
    if (form.pandi_org_id && !form.vessel.trim()) return toast.error("Vessel is mandatory when a P&I club is selected");
    setBusy(true);
    try {
      await api.put(`/candidates/${candidate.id}/services`, {
        client_org_id: form.client_org_id, service_ids: form.service_ids, rank: form.rank.trim(),
        pandi_org_id: form.pandi_org_id, vessel: form.vessel.trim(),
      });
      refreshCandidate(candidate.id);
      setStep(3);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sendRemark = async () => {
    if (!remark.trim() || !candidate) return;
    try {
      await api.post(`/candidates/${candidate.id}/remarks`, { text: remark });
      toast.success("Remark sent");
      setRemark("");
    } catch (e) { toast.error(apiError(e)); }
  };

  const docsByCategory = (key) => (candidate?.documents || []).filter((d) => d.category === key);
  const visibleDocs = (key) => docsByCategory(key).filter((d) => !(hiddenDocIds && hiddenDocIds.has(d.id)));
  const REQUIRED_DOC_CATS = [["declaration", "Candidate Declaration Form"], ["ilo_page", "ILO Page"], ["medical_reports", "Medical Reports"], ["consent_form", "MedVerify Consent Form"]];
  const missingRequiredDocs = REQUIRED_DOC_CATS.filter(([k]) => !visibleDocs(k).some((d) => !d.superseded)).map(([, l]) => l);
  const isRevisitCase = !!candidate && (candidate.is_final || candidate.status !== "pending");
  const lockCore = isEdit && user.role === "clinic";
  const [cancelMode, setCancelMode] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const canCancel = !!candidate && candidate.status === "pending" && !candidate.is_final &&
    (user.role === "admin" || candidate.clinic_org_id === user.organization_id);
  const isReturningCase = !!candidate && (candidate.history || []).length > 0;

  const cancelRegistration = async () => {
    try {
      const { data } = await api.delete(`/candidates/${candidate.id}/registration`);
      if (data.removed === "visit") {
        toast.success("Visit cancelled — case restored to its previous state");
        navigate(`/candidates/${candidate.id}`);
      } else {
        toast.success("Registration cancelled — entry removed");
        navigate("/candidates");
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  const cancelBlock = canCancel && (
    <div className="mt-4" data-testid="cancel-registration-box">
      {!cancelMode ? (
        <Button type="button" variant="danger" size="sm" onClick={() => setCancelMode(true)} data-testid="cancel-registration-btn">
          Cancel Registration (For Wrong Entry or Incorrect Document Upload)
        </Button>
      ) : (
        <div className="space-y-3 rounded-md border border-red-200 bg-red-50 p-4 fade-up">
          <label className="flex cursor-pointer items-start gap-2 text-sm text-red-700">
            <input type="checkbox" checked={cancelConfirm} onChange={(e) => setCancelConfirm(e.target.checked)} className="mt-0.5 accent-red-600" data-testid="cancel-confirm-checkbox" />
            {isReturningCase
              ? "I confirm this visit should be cancelled. Documents and details of this visit will be removed and the case will return to its previous verdict. Previous history is preserved."
              : "I confirm this entry was filled in wrong and should be permanently removed. It will not be stored in the system."}
          </label>
          <div className="flex gap-2">
            <Button type="button" variant="danger" size="sm" disabled={!cancelConfirm} onClick={cancelRegistration} data-testid="confirm-cancel-registration-btn">{isReturningCase ? "Cancel This Visit" : "Remove Entry"}</Button>
            <Button type="button" variant="outline" size="sm" onClick={() => { setCancelMode(false); setCancelConfirm(false); }} data-testid="keep-registration-btn">Keep</Button>
          </div>
        </div>
      )}
    </div>
  );

  const canUploadCat = (key) => {
    if (!candidate) return false;
    if (user.role === "admin") return true;
    if (user.role !== "clinic") return false;
    if (isEdit && isRevisitCase) return true;
    if (candidate.status === "temp_unfit") return true;
    if (candidate.is_final) return false;
    if (key === "additional_tests") return true;
    const active = docsByCategory(key).filter((d) => !d.superseded);
    return active.length === 0 || candidate.reupload_open;
  };

  if (user.role !== "clinic" && user.role !== "admin") return <p className="text-slate-500">Not authorized.</p>;

  const blocked = dupes.length > 0 && !isEdit;

  return (
    <div className="max-w-3xl">
      <PageHeader
        title={isEdit ? "Edit Candidate" : "Register Candidate"}
        subtitle={step === 1 ? (lockCore ? "Name, Date of Birth and INDOS cannot be changed after registration" : "Mandatory fields are marked with *") : undefined}
      />
      {!isEdit && <StepIndicator step={step} />}

      {step === 1 && (
        <form onSubmit={submitStep1}>
          <Card className="space-y-6 p-8">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field label="Candidate Name" required>
                <Input value={form.name} onChange={set("name")} required disabled={lockCore} className={lockCore ? "!bg-slate-100 !text-slate-500" : ""} data-testid="candidate-name-input" />
              </Field>
              <Field label="Candidate Company ID">
                <Input value={form.company_id} onChange={set("company_id")} data-testid="candidate-company-id-input" />
              </Field>
              {isEdit && user.role === "admin" && (
                <Field label="Rank" required>
                  <Input list="ranks-list" value={form.rank} onChange={set("rank")} placeholder="Select or type a rank" required data-testid="candidate-rank-input" />
                  <datalist id="ranks-list">
                    {ranks.map((r) => <option key={r.id} value={r.name} />)}
                  </datalist>
                </Field>
              )}
              <Field label="Date of Birth" required>
                <Input type="date" value={form.dob} onChange={set("dob")} disabled={lockCore} className={lockCore ? "!bg-slate-100 !text-slate-500" : ""} min={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 106); return d.toISOString().slice(0, 10); })()} max={new Date().toISOString().slice(0, 10)} required data-testid="candidate-dob-input" />
                {age !== null && <p className="mt-1 text-xs font-semibold text-teal" data-testid="candidate-age">Age: {age} years</p>}
              </Field>
              <Field label="Mobile Number" required>
                <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} inputMode="numeric" placeholder="10-digit mobile number" required data-testid="candidate-mobile-input" />
              </Field>
              <Field label="INDOS Number">
                <Input value={form.indos_number} onChange={(e) => setForm({ ...form, indos_number: e.target.value.toUpperCase() })} disabled={lockCore} className={lockCore ? "!bg-slate-100 !text-slate-500" : ""} data-testid="candidate-indos-input" />
              </Field>
              <Field label="CDC Number">
                <Input value={form.cdc_number} onChange={(e) => setForm({ ...form, cdc_number: e.target.value.toUpperCase() })} data-testid="candidate-cdc-input" />
              </Field>
              {user.role === "admin" && (
                <Field label="Clinic">
                  <Select value={form.clinic_org_id} onChange={set("clinic_org_id")} data-testid="clinic-select">
                    <option value="">No clinic (optional)</option>
                    {clinics.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                </Field>
              )}
              {isEdit && user.role === "admin" && (
                <Field label="Client (Shipping Company)" required>
                  <Select value={form.client_org_id} onChange={set("client_org_id")} required data-testid="client-select-edit">
                    <option value="">Select client...</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                </Field>
              )}
              {isEdit && user.role === "admin" && (
                <Field label="P&I Club">
                  <Select value={form.pandi_org_id} onChange={set("pandi_org_id")} data-testid="pandi-select-edit">
                    <option value="">No P&I club</option>
                    {pandiClubs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </Select>
                </Field>
              )}
              {isEdit && user.role === "admin" && form.pandi_org_id && (
                <Field label="Vessel" required>
                  <Input value={form.vessel} onChange={(e) => setForm({ ...form, vessel: e.target.value.toUpperCase() })} placeholder="Type the vessel name" data-testid="vessel-input-edit" />
                </Field>
              )}
            </div>

            {!isEdit && dupes.length === 0 && rosterMatches.length > 0 && (
              <div className="rounded-md border border-teal/40 bg-teal-light/30 p-4 fade-up" data-testid="roster-suggestions">
                <p className="flex items-center gap-2 text-sm font-bold text-navy">
                  <Users size={15} /> Found in imported crew list — click to auto-fill
                </p>
                <ul className="mt-3 space-y-2">
                  {rosterMatches.map((m) => (
                    <li key={m.id}>
                      <button
                        type="button"
                        onClick={() => fillFromRoster(m)}
                        className="flex w-full flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-left text-sm transition-colors hover:border-teal hover:bg-teal-light/40"
                        data-testid={`roster-suggestion-${m.id}`}
                      >
                        <span className="font-semibold text-slate-900">{m.name}</span>
                        <span className="text-xs text-slate-500">
                          {[m.rank, m.indos_number ? `INDOS ${m.indos_number}` : "", m.mobile, m.dob ? `DOB ${fmtDate(m.dob)}` : ""].filter(Boolean).join(" · ")}
                        </span>
                        {m.client_name && <span className="ml-auto shrink-0 text-xs font-bold text-teal">{m.client_name}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {dupes.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-4 fade-up" data-testid="duplicate-warning">
                <p className="flex items-center gap-2 text-sm font-bold text-amber-800">
                  <AlertTriangle size={15} /> This candidate already exists (matching INDOS / CDC / mobile)
                </p>
                <p className="mt-1 text-xs text-amber-700">A new candidate cannot be created. Open the existing record to select the company & services and upload new documents.</p>
                <ul className="mt-3 space-y-2">
                  {dupes.map((d) => (
                    <li key={d.id} className="flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-white px-3 py-2 text-sm">
                      <span className="font-semibold text-slate-900">{d.name}</span>
                      <span className="text-xs text-slate-500">{d.rank} · {d.mobile}{d.indos_number ? ` · INDOS ${d.indos_number}` : ""} · DOB {fmtDate(d.dob)}</span>
                      <div className="ml-auto flex items-center gap-3">
                        <Link to={`/candidates/${d.id}/edit`} className="flex items-center gap-1 text-xs font-bold text-teal hover:text-navy" data-testid={`upload-duplicate-${d.id}`}>
                          Open & continue <ArrowRight size={12} />
                        </Link>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {isEdit && user.role === "admin" && form.client_org_id && (
              <div className="fade-up">
                <p className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Services for this client *</p>
                {services.length === 0 ? (
                  <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                    No services configured for this client yet. Contact admin.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="services-list-edit">
                    {services.map((s) => (
                      <label
                        key={s.id}
                        className={`flex cursor-pointer items-center gap-3 rounded-md border px-4 py-3 text-sm transition-colors ${
                          form.service_ids.includes(s.id) ? "border-teal bg-teal-light/50 font-semibold text-navy" : "border-slate-200 text-slate-700 hover:bg-slate-50"
                        }`}
                        data-testid={`service-option-edit-${s.id}`}
                      >
                        <input type="checkbox" checked={form.service_ids.includes(s.id)} onChange={() => toggleService(s.id)} className="accent-teal" />
                        <span className="flex-1">
                          {s.name}
                          {s.description && <span className="mt-0.5 block text-xs font-normal text-slate-500">{s.description}</span>}
                        </span>
                        {s.cost !== undefined && <span className="ml-auto text-xs text-slate-500">₹{s.cost}</span>}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 border-t border-slate-100 pt-6">
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
              <Button type="submit" disabled={busy || blocked} data-testid="register-candidate-submit-btn">
                {busy ? "Saving..." : isEdit ? (user.role === "clinic" ? "Save & Continue" : "Save Changes") : "Register & Continue"}
              </Button>
            </div>
            {blocked && <p className="text-right text-xs text-amber-600">This candidate already exists — open the existing record above to continue.</p>}
          </Card>
        </form>
      )}
      {step === 1 && cancelBlock}

      {step === 2 && candidate && (
        <div className="space-y-6 fade-up">
          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-slate-900">{candidate.name}</h3>
          </Card>

          <Card className="space-y-5 p-6">
            <div>
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Rank *</p>
              <Input list="ranks-list-step2" value={form.rank} onChange={set("rank")} placeholder="Select or type a rank" data-testid="step2-rank-input" />
              <datalist id="ranks-list-step2">
                {ranks.map((r) => <option key={r.id} value={r.name} />)}
              </datalist>
            </div>
            <div>
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Client (Shipping Company) *</p>
              <Select value={form.client_org_id} onChange={set("client_org_id")} data-testid="client-select">
                <option value="">Select client...</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </div>
            <div>
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">P&amp;I Club (optional)</p>
              <Select value={form.pandi_org_id} onChange={set("pandi_org_id")} data-testid="pandi-select">
                <option value="">No P&amp;I club</option>
                {pandiClubs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            </div>
            {form.pandi_org_id && (
              <div className="fade-up">
                <p className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Vessel *</p>
                <Input value={form.vessel} onChange={(e) => setForm({ ...form, vessel: e.target.value.toUpperCase() })} placeholder="Type the vessel name" data-testid="vessel-input" />
                <p className="mt-1 text-xs text-amber-600">Vessel is mandatory when a P&amp;I club is selected</p>
              </div>
            )}
            {form.client_org_id && (
              <div className="fade-up">
                <p className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
                  Services for this client *
                </p>
                {services.length === 0 ? (
                  <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                    No services configured for this client yet. Contact admin.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="services-list">
                    {services.map((s) => (
                      <label
                        key={s.id}
                        className={`flex cursor-pointer items-center gap-3 rounded-md border px-4 py-3 text-sm transition-colors ${
                          form.service_ids.includes(s.id) ? "border-teal bg-teal-light/50 font-semibold text-navy" : "border-slate-200 text-slate-700 hover:bg-slate-50"
                        }`}
                        data-testid={`service-option-${s.id}`}
                      >
                        <input type="checkbox" checked={form.service_ids.includes(s.id)} onChange={() => toggleService(s.id)} className="accent-teal" />
                        <span className="flex-1">
                          {s.name}
                          {s.description && <span className="mt-0.5 block text-xs font-normal text-slate-500">{s.description}</span>}
                        </span>
                        {s.cost !== undefined && <span className="ml-auto text-xs text-slate-500">₹{s.cost}</span>}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>

          <div className="flex items-center justify-between gap-4">
            <div>{cancelBlock}</div>
            <Button onClick={saveServices} disabled={busy} data-testid="step2-continue-btn">
              {busy ? "Saving..." : "Save & Continue"} <ArrowRight size={15} />
            </Button>
          </div>
        </div>
      )}

      {step === 3 && candidate && (
        <div className="space-y-6 fade-up">
          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-slate-900">{candidate.name}</h3>
          </Card>

          <Card className="divide-y divide-slate-100">
            {CATEGORIES.map((c) => (
              <div key={c.key} className="px-6 py-5">
                <DocUploadSlot
                  candidateId={candidate.id}
                  category={c.key}
                  label={c.label}
                  hint={c.hint}
                  accept={c.accept}
                  docs={visibleDocs(c.key)}
                  canUpload={canUploadCat(c.key)}
                  revisit={isEdit && isRevisitCase}
                  isAdmin={user.role === "admin"}
                  onChanged={() => refreshCandidate(candidate.id)}
                />
              </div>
            ))}
          </Card>

          <Card className="p-6">
            <p className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-700"><MessageSquare size={15} className="text-navy" /> Remark (optional)</p>
            <div className="flex gap-2">
              <Input placeholder="Add a remark for the doctor..." value={remark} onChange={(e) => setRemark(e.target.value)} data-testid="step2-remark-input" />
              <Button variant="outline" onClick={sendRemark} disabled={!remark.trim()} data-testid="step2-send-remark-btn">Send</Button>
            </div>
          </Card>

          <div className="space-y-3">
            {missingRequiredDocs.length > 0 && (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800" data-testid="finish-blocked-note">
                Upload the required documents to finish: {missingRequiredDocs.join(", ")}
              </p>
            )}
            <div className="flex items-center justify-between gap-4">
              <div>{cancelBlock}</div>
              <Button onClick={() => navigate(`/candidates/${candidate.id}`)} disabled={missingRequiredDocs.length > 0} data-testid="finish-registration-btn">
                Finish <ArrowRight size={15} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
